import type { StationKind, ZonePayload } from '../../net/protocol';
import { isWalkable, tileAt, tileCentre } from './layout';

/**
 * Where the town's detail stands, worked out from the layout the server sent.
 *
 * Pure, and deliberately Babylon-free — the same split `models/poses.ts` and
 * `models/armour.ts` have. Everything here answers *where does a thing go and which
 * way does it point*; `models/scenery.ts` answers *what is it made of*. The whole
 * file is a function of the `ZONE` frame, so a shopfront cannot get out of step with
 * the town the server is running.
 *
 * **Nothing here is an entity.** A shopkeeper, a shop sign, a lamp post and a lit
 * window are all *scenery drawn from the layout*: they are not in the snapshot, they
 * are not simulated, they have no id and they are not collidable. See
 * `HUB-ZONES-SCRAP-UI.md` for why, and for what Phase 13's vendors would have to
 * change.
 */

/** The four grid directions. `NORTH` is `row - 1`, which is `-z`. */
export type Facing = 'NORTH' | 'SOUTH' | 'EAST' | 'WEST';

/** The grid step for each facing, as `[dcol, drow]`. */
export const FACING_STEP: Readonly<Record<Facing, readonly [number, number]>> = {
  NORTH: [0, -1],
  SOUTH: [0, 1],
  EAST: [1, 0],
  WEST: [-1, 0],
};

/**
 * The yaw that turns a piece's local `+z` to point this way.
 *
 * Every scenery part is authored in a local frame whose `+z` is its **front** — the
 * way the shopkeeper looks, the way a door opens — so one yaw orients a whole shop.
 */
export const FACING_YAW: Readonly<Record<Facing, number>> = {
  SOUTH: 0,
  EAST: Math.PI / 2,
  NORTH: Math.PI,
  WEST: -Math.PI / 2,
};

/** The direction opposite this one. */
export function opposite(facing: Facing): Facing {
  switch (facing) {
    case 'NORTH':
      return 'SOUTH';
    case 'SOUTH':
      return 'NORTH';
    case 'EAST':
      return 'WEST';
    default:
      return 'EAST';
  }
}

/**
 * The facings whose faces the camera can actually see, best first.
 *
 * The gameplay camera sits at alpha `-π/4`, beta `π/4` — which puts it **north-east**
 * of its target, about 13 units each way and 18 up. A box therefore only ever shows
 * its `+x` (east) and `-z` (north) faces, so a door carved into a south wall is a
 * door nobody in this game will ever see. Ordering the choice by this rather than by
 * a fixed `N,S,E,W` is the difference between a town of houses and a town of blank
 * boxes with the doors on the far side.
 */
export const CAMERA_FACING_ORDER: readonly Facing[] = ['NORTH', 'EAST', 'SOUTH', 'WEST'];

/**
 * The facings a box actually shows at this camera — the first two of the order above.
 *
 * Its own constant because it answers a **different question** from `CAMERA_FACING_ORDER`, and
 * conflating them is what let invisible windows be built. The order says *which face to prefer*
 * for the one door a house gets, and it lists all four because a house enclosed on its north and
 * east sides still needs a door somewhere. This says *which faces are ever on screen*, and the
 * answer is two: the camera sits north-east, so only `+x` (east) and `-z` (north) are turned
 * toward it.
 *
 * Used to skip building the south and west faces entirely. They were never visible, and their lit
 * windows bloomed **through** the house — `GlowLayer` composites additively with no depth test, so
 * a window on the far side brightens the near wall. See the note at the call site in `render.ts`.
 */
export const CAMERA_VISIBLE_FACINGS: readonly Facing[] = ['NORTH', 'EAST'];

/** Rotates a local offset by a yaw. Left-handed, matching Babylon's `rotation.y`. */
export function rotateLocal(
  local: readonly [number, number, number],
  yaw: number,
): [number, number, number] {
  const s = Math.sin(yaw);
  const c = Math.cos(yaw);
  return [local[0] * c + local[2] * s, local[1], -local[0] * s + local[2] * c];
}

/**
 * A hash of a grid cell, in `[0, 1)`.
 *
 * The same discipline `game/zone/textures.ts` follows: a town must look identical on
 * a rebuild and identical for two players standing in it, so anything that varies
 * varies by **hash of the cell**, never by a random draw. There is no seed to hash
 * against either — the hub is fixed, not seeded.
 */
export function cellHash(col: number, row: number): number {
  let h = (col * 374761393 + row * 668265263) | 0;
  h = (h ^ (h >>> 13)) | 0;
  h = Math.imul(h, 1274126177) | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** A workshop's stall: where it stands and which way it looks. */
export interface Shopfront {
  kind: StationKind;
  col: number;
  row: number;
  /** The centre of the street tile the stall stands on. */
  at: { x: number; z: number };
  /** The way the counter faces — out into the street. */
  facing: Facing;
  yaw: number;
  /** True when the stall backs onto a house rather than standing free. */
  backed: boolean;
}

/**
 * One stall per station the server sent.
 *
 * The stall is placed **on the station's own street tile**, because a station is a
 * position and not a tile kind: the floor under a shopfront stays street, so nothing
 * about walkability, wall drawing or spawn weight moves for a shop and the client's
 * `isWalkable`/`isSolid` mirror stays exactly where it is.
 *
 * Which way it faces is chosen in two steps, and both matter:
 *
 * 1. **It must open onto walkable ground.** A counter facing a wall 0.9 units away is
 *    a counter nobody can stand at.
 * 2. **It prefers to back onto a house.** A shop built against a building reads as a
 *    shopfront; the same geometry in the middle of a road reads as a barricade. Four
 *    of the five have a house behind them and the Arcane Vault, at the crossroads,
 *    does not — so `backed` is false for it and it is honestly a market stall.
 *
 * Ties break on `CAMERA_FACING_ORDER`, so a shop shows the camera its front where it
 * has the choice.
 */
export function shopfronts(zone: ZonePayload): Shopfront[] {
  const fronts: Shopfront[] = [];

  for (const station of zone.stations ?? []) {
    let best: { facing: Facing; backed: boolean; rank: number } | null = null;

    for (const [index, facing] of CAMERA_FACING_ORDER.entries()) {
      const [dc, dr] = FACING_STEP[facing];
      const front = tileAt(zone, station.col + dc, station.row + dr);
      if (!isWalkable(front.kind)) continue;

      const [bc, br] = FACING_STEP[opposite(facing)];
      const behind = tileAt(zone, station.col + bc, station.row + br);
      const backed = behind.kind === 'BUILDING';

      // Backed first, then the camera's own ordering.
      const rank = (backed ? 0 : 10) + index;
      if (!best || rank < best.rank) best = { facing, backed, rank };
    }

    // A station walled in on all four sides cannot have a stall pointed anywhere, and
    // drawing one facing into rock would be worse than drawing none. It cannot happen
    // in the hub as authored — a Go test holds that — so this is the defined answer
    // to a layout that changed, not a case anybody should hit.
    if (!best) continue;

    fronts.push({
      kind: station.kind,
      col: station.col,
      row: station.row,
      at: tileCentre(zone, station.col, station.row),
      facing: best.facing,
      yaw: FACING_YAW[best.facing],
      backed: best.backed,
    });
  }

  return fronts;
}

/** One outward face of a house that a street runs past. */
export interface HouseFace {
  col: number;
  row: number;
  /** The centre of the building's own tile. */
  at: { x: number; z: number };
  /** The outward normal of this face. */
  facing: Facing;
  yaw: number;
  /** True for the one face that carries the door. */
  door: boolean;
}

/**
 * Every house face with a street in front of it.
 *
 * A face with nothing walkable beyond it gets nothing at all — the four corner
 * houses in the hub are enclosed by their neighbours and the curtain wall on all
 * four sides, so they are blank blocks and correctly so: a door onto rock is a door
 * onto rock.
 *
 * Exactly one face per house carries the door, the first in `CAMERA_FACING_ORDER`,
 * so the door is on a face the player can see wherever the house allows it.
 *
 * **Doors and windows are geometry on a building, and a building already draws its
 * own block** — so every part here straddles the block's face rather than sitting
 * flush against it. Two coplanar surfaces in one place is the failure this area has
 * produced before: it showed as a glowing kerb along the foot of every house, when a
 * wall was drawn on a boundary a structure already filled.
 */
export function houseFaces(zone: ZonePayload): HouseFace[] {
  const faces: HouseFace[] = [];

  for (const tile of zone.tiles) {
    if (tile.kind !== 'BUILDING') continue;

    const open = CAMERA_FACING_ORDER.filter((facing) => {
      const [dc, dr] = FACING_STEP[facing];
      return isWalkable(tileAt(zone, tile.col + dc, tile.row + dr).kind);
    });
    if (open.length === 0) continue;

    const at = tileCentre(zone, tile.col, tile.row);
    for (const [index, facing] of open.entries()) {
      faces.push({
        col: tile.col,
        row: tile.row,
        at,
        facing,
        yaw: FACING_YAW[facing],
        door: index === 0,
      });
    }
  }

  return faces;
}

/** A house's chimney, or null when this one has none. */
export interface Chimney {
  col: number;
  row: number;
  at: { x: number; z: number };
  /** Offset from the tile centre, in world units — a chimney sits on a corner. */
  offset: { x: number; z: number };
}

/**
 * Which houses have a chimney, and on which corner.
 *
 * Two thirds of them, by cell hash — a roofline where every house is identical reads
 * as tiling rather than as a street, and one where every house has one reads as a
 * factory. Hashed rather than drawn, for the reason every other varying thing in a
 * zone is: the town must look the same on a rebuild and the same for two players.
 */
export function chimneys(zone: ZonePayload, cornerOffset: number): Chimney[] {
  const out: Chimney[] = [];

  for (const tile of zone.tiles) {
    if (tile.kind !== 'BUILDING') continue;

    const h = cellHash(tile.col, tile.row);
    if (h >= 0.66) continue;

    // The low two thirds of the hash also choose the corner, so one draw does both.
    const corner = Math.floor((h / 0.66) * 4) % 4;
    out.push({
      col: tile.col,
      row: tile.row,
      at: tileCentre(zone, tile.col, tile.row),
      offset: {
        x: corner === 0 || corner === 3 ? -cornerOffset : cornerOffset,
        z: corner < 2 ? -cornerOffset : cornerOffset,
      },
    });
  }

  return out;
}

/** A street lamp, already offset off the middle of the road. */
export interface LampPost {
  at: { x: number; z: number };
}

/**
 * Lamps on the street corners around the central plaza.
 *
 * The rule is derived rather than authored: a **walkable tile with houses on two or
 * more sides** is a corner, and a corner is where a lamp goes. In the hub as drawn
 * that is six tiles, forming a ring round the crossroads, and it stays true if the
 * plan changes shape.
 *
 * The lamp is pushed `kerb` units off the tile centre toward the first of those
 * houses, so it hugs the kerb rather than standing in the middle of the road. That
 * matters for one reason: **scenery is not collision**. The server does not know a
 * lamp is there and a station is a position rather than a tile kind, so a player
 * *can* walk through it — and the only defence against that reading as a bug is to
 * put nothing where a player walks. The same offset keeps every stall against its
 * house and out of the road.
 *
 * A station's own tile is excluded: it already has a shopfront on it, and a lamp post
 * through an awning is the two-things-in-one-place fault in miniature.
 */
export function lampPosts(zone: ZonePayload, kerb: number): LampPost[] {
  const taken = new Set((zone.stations ?? []).map((s) => `${s.col},${s.row}`));
  const posts: LampPost[] = [];

  for (const tile of zone.tiles) {
    if (!isWalkable(tile.kind)) continue;
    if (taken.has(`${tile.col},${tile.row}`)) continue;

    const houses = CAMERA_FACING_ORDER.filter((facing) => {
      const [dc, dr] = FACING_STEP[facing];
      return tileAt(zone, tile.col + dc, tile.row + dr).kind === 'BUILDING';
    });
    if (houses.length < 2) continue;

    const [dc, dr] = FACING_STEP[houses[0] as Facing];
    const centre = tileCentre(zone, tile.col, tile.row);
    posts.push({ at: { x: centre.x + dc * kerb, z: centre.z + dr * kerb } });
  }

  return posts;
}

/** A merlon on top of the curtain wall. */
export interface Merlon {
  at: { x: number; z: number };
}

/**
 * The battlements: four blocks on top of every rampart tile.
 *
 * A curtain wall drawn as a smooth 4.6-unit slab reads as a retaining wall. Notching
 * the top is what says *fortification*, and it is the cheapest detail in this whole
 * file — the merlons are on a `spacing` grid that lines up across tile boundaries, so
 * a run of wall gets one continuous crenellation rather than a repeating motif with a
 * seam at every cell.
 *
 * The gate is a `GATE` tile rather than a `RAMPART`, so the notch above the way in
 * comes for free.
 */
export function merlons(zone: ZonePayload, spacing: number): Merlon[] {
  const out: Merlon[] = [];

  for (const tile of zone.tiles) {
    if (tile.kind !== 'RAMPART') continue;

    const centre = tileCentre(zone, tile.col, tile.row);
    for (const dx of [-spacing, spacing]) {
      for (const dz of [-spacing, spacing]) {
        out.push({ at: { x: centre.x + dx, z: centre.z + dz } });
      }
    }
  }

  return out;
}
